import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TrackBar } from '@digital-twins/deck-ui/components'
import { CategoricalSharePayload, CategoricalSharePlotView } from '@digital-twins/deck-ui/plots/categorical-share'
import { ChoicePlotView, donutAlternative } from '@digital-twins/deck-ui/plots/choice-bars'
import { OtherToggleLabels, ShareUnit, ShareUnitToggle } from '@digital-twins/deck-ui/plots/_shared'

import {
  FigurePanel,
  FigureViewProps,
  OTHER_COLOR,
  fillPlotLabel,
  formatStampedNumber,
  formatStampedShare,
  useShareUnitLabels,
} from './figureViewShared'

import { ChoiceBarsData, ChoiceBarsSeries, PlotHighlight } from '@digital-twins/slide-schemas/generated/plot-types'

/**
 * `choice-bars` on the survey tab (plots plan J). Total draws the kind's own
 * plot component, the one the summary card and quick-study draw
 * (`ChoicePlotView`), with the bars / donut toggle when Python stamped a
 * share-donut alternative. A cut draws the figure service's `cutPlot` (Wave 7
 * S3): one `grouped-bars` plot, each option a group with one bar per cut
 * group, through the library's categorical view; a multi-series (lane) read
 * draws `cutPlots`, one such plot per lane under the lane's title. Shape only:
 * shares, counts, order, crown and the "Other" row are Python's.
 *
 * LEGACY READER (`ChoiceCut`): a cut response with neither (one the service
 * could not form) keeps today's option-by-option list of the groups' own
 * plots.
 */

/** The figure payload as Python stamps it: the plot, its render props and its alternatives. */
interface StampedChoicePlot {
  data: ChoiceBarsData
  props?: { highlight?: PlotHighlight }
  alternatives?: unknown
}

function stampedPlot(panel: FigurePanel): StampedChoicePlot {
  return panel.plot as unknown as StampedChoicePlot
}

function choiceData(panel: FigurePanel): ChoiceBarsData {
  return stampedPlot(panel).data
}

interface ChoiceFootnotesProps {
  data: ChoiceBarsData
  testIdPrefix: string
}

function ChoiceFootnotes(props: ChoiceFootnotesProps) {
  const { data, testIdPrefix } = props

  const truncation = data.truncation
    ? fillPlotLabel(data.labels.n_of_total_shown, { shown: data.truncation.shown, total: data.truncation.total })
    : null
  const multiSelect = data.multiSelect ? data.labels.multi_select_note ?? null : null
  if (!truncation && !multiSelect) {
    return null
  }
  return (
    <div className="space-y-1 text-center" data-testid={`${testIdPrefix}-notes`}>
      {truncation && <p className="text-sm text-muted-foreground italic">{truncation}</p>}
      {multiSelect && <p className="text-xs text-muted-foreground">{multiSelect}</p>}
    </div>
  )
}

interface ChoiceTotalProps {
  plot: StampedChoicePlot
  locale: string
  testIdPrefix: string
  /** The "Other" link's words (plots plan OT); the plot draws no link without them. */
  otherToggleLabels?: OtherToggleLabels
  shareUnit?: ShareUnit
}

/**
 * Total: the kind's plot with its bars / donut toggle; a read with several
 * series (lanes) draws one titled plot per series, bars only.
 */
function ChoiceTotal(props: ChoiceTotalProps) {
  const { plot, locale, testIdPrefix, otherToggleLabels, shareUnit } = props

  const { t } = useTranslation('app')

  const data = plot.data
  const highlight = plot.props?.highlight
  if (data.series.length <= 1) {
    return (
      <ChoicePlotView
        data={data}
        donut={donutAlternative(plot.alternatives)}
        highlight={highlight}
        toggleLabels={{ bars: t('studyReport.plotToggle.bars'), donut: t('studyReport.plotToggle.donut') }}
        otherToggleLabels={otherToggleLabels}
        shareUnit={shareUnit}
        locale={locale}
        testIdPrefix={`${testIdPrefix}-plot`}
      />
    )
  }
  return (
    <div className="space-y-8">
      {data.series.map((series, index) => (
        <section key={series.id} className="space-y-3" data-testid={`${testIdPrefix}-lane-${index}`}>
          <div className="flex items-baseline gap-2 text-sm font-semibold text-foreground">
            <span>{series.label ?? t('studyReport.questionCard.laneFallbackTitle', { n: index + 1 })}</span>
            {series.lowBase && data.labels.plot_low_base && (
              <span className="text-xs font-normal text-muted-foreground">{data.labels.plot_low_base}</span>
            )}
          </div>
          <ChoicePlotView
            data={{ ...data, series: [series] }}
            highlight={highlight}
            otherToggleLabels={otherToggleLabels}
            shareUnit={shareUnit}
            locale={locale}
            testIdPrefix={`${testIdPrefix}-lane-${index}-plot`}
          />
        </section>
      ))}
    </div>
  )
}

function printedChoiceShare(
  item: { share: number; count?: number | null } | undefined,
  decimals: number,
  locale: string,
  shareUnit: ShareUnit,
): string | null {
  if (!item) {
    return null
  }
  if (shareUnit === 'count' && typeof item.count === 'number') {
    return formatStampedNumber(item.count, 0, locale)
  }
  return formatStampedShare(item.share, decimals, locale)
}

interface ChoiceCutProps {
  panels: Array<FigurePanel>
  /** Series are joined by position: a group names its single series after itself, lanes keep roster order. */
  seriesIndex: number
  colors: Array<string>
  locale: string
  testIdPrefix: string
  shareUnit?: ShareUnit
}

/**
 * LEGACY READER, a cut without `cutPlot`: each option (the first group's rows,
 * Python's order), then each group's share of it (today's segmented list).
 * Items are joined by option id.
 */
function ChoiceCut(props: ChoiceCutProps) {
  const { panels, seriesIndex, colors, locale, testIdPrefix, shareUnit = 'percent' } = props

  const seriesOf = (panel: FigurePanel): ChoiceBarsSeries | undefined => choiceData(panel).series[seriesIndex]
  const leading = seriesOf(panels[0])
  if (!leading) {
    return null
  }
  return (
    <div className="w-full space-y-8 overflow-hidden pr-4">
      {leading.items.map((item, itemIndex) => (
        <div key={item.id} className="space-y-3" data-testid={`${testIdPrefix}-option-${itemIndex}`}>
          <div className="text-sm font-semibold text-foreground">{item.label}</div>
          <div className="space-y-3 pl-2">
            {panels.map((panel, groupIndex) => {
              const data = choiceData(panel)
              const groupItem = seriesOf(panel)?.items.find((candidate) => candidate.id === item.id)
              const color = item.role === 'other' ? OTHER_COLOR : colors[groupIndex % Math.max(colors.length, 1)]
              const share = printedChoiceShare(groupItem, data.decimals, locale, shareUnit)
              return (
                <div
                  key={panel.group.id}
                  className="space-y-1"
                  data-testid={`${testIdPrefix}-option-${itemIndex}-group-${groupIndex}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">
                      {panel.group.label ?? ''}
                      {panel.group.lowBase && data.labels.plot_low_base ? (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          {data.labels.plot_low_base}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-sm font-semibold text-muted-foreground tabular-nums">{share ?? ''}</span>
                  </div>
                  <TrackBar
                    widthPct={groupItem?.share ?? 0}
                    color={color}
                    value={share ?? ''}
                    label={`${panel.group.label ?? ''}: ${item.label}`}
                  />
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export function ChoiceBarsFigure(props: FigureViewProps) {
  const { question, panels, isCut, colors, locale, otherToggleLabels } = props

  const { t } = useTranslation('app')
  const [shareUnit, setShareUnit] = useState<ShareUnit>('percent')
  const shareUnitLabels = useShareUnitLabels()

  const first = choiceData(panels[0])
  const testIdPrefix = 'figure-choice-bars'
  const unit = (
    <div className="mb-3 flex justify-end">
      <ShareUnitToggle value={shareUnit} onChange={setShareUnit} labels={shareUnitLabels} testIdPrefix={`${testIdPrefix}-share-unit-`} />
    </div>
  )

  if (!isCut) {
    // The plot prints its own notes (truncation, multi-select) under the bars.
    return (
      <div className="w-full" data-testid={testIdPrefix}>
        {unit}
        <ChoiceTotal plot={stampedPlot(panels[0])} locale={locale} testIdPrefix={testIdPrefix} otherToggleLabels={otherToggleLabels} shareUnit={shareUnit} />
      </div>
    )
  }
  const lanePlots = question.cutPlots ?? []
  if (lanePlots.length > 0 && lanePlots.every((lane) => lane.plot.kind === 'grouped-bars')) {
    return (
      <div className="w-full space-y-8" data-testid={testIdPrefix}>
        {unit}
        {lanePlots.map((lane, index) => (
          <section key={lane.seriesId} className="space-y-3" data-testid={`${testIdPrefix}-lane-${index}`}>
            <div className="text-sm font-semibold text-foreground" data-testid={`${testIdPrefix}-lane-${index}-title`}>
              {lane.label ?? t('studyReport.questionCard.laneFallbackTitle', { n: index + 1 })}
            </div>
            <CategoricalSharePlotView
              plot={lane.plot as unknown as CategoricalSharePayload}
              size="report"
              locale={locale}
              renderMode="interactive"
              otherToggleLabels={otherToggleLabels}
              shareUnit={shareUnit}
              fastHover
              testIdPrefix={`${testIdPrefix}-lane-${index}-cut-plot`}
            />
          </section>
        ))}
      </div>
    )
  }
  if (question.cutPlot?.kind === 'grouped-bars') {
    return (
      <div className="w-full" data-testid={testIdPrefix}>
        {unit}
        <CategoricalSharePlotView
          plot={question.cutPlot as unknown as CategoricalSharePayload}
          size="report"
          locale={locale}
          renderMode="interactive"
          otherToggleLabels={otherToggleLabels}
          shareUnit={shareUnit}
          fastHover
          testIdPrefix={`${testIdPrefix}-cut-plot`}
        />
      </div>
    )
  }
  return (
    <div className="w-full space-y-8" data-testid={testIdPrefix}>
      {unit}
      {first.series.map((series, index) => (
        <section key={series.id} className="space-y-3" data-testid={`${testIdPrefix}-lane-${index}`}>
          {first.series.length > 1 && (
            <div className="text-sm font-semibold text-foreground">{series.label ?? ''}</div>
          )}
          <ChoiceCut
            panels={panels}
            seriesIndex={index}
            colors={colors}
            locale={locale}
            shareUnit={shareUnit}
            testIdPrefix={first.series.length > 1 ? `${testIdPrefix}-lane-${index}` : testIdPrefix}
          />
        </section>
      ))}
      <ChoiceFootnotes data={first} testIdPrefix={testIdPrefix} />
    </div>
  )
}
